import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from '../../../../shared/i18n';
import { useActivitySession } from '../../hooks/useActivitySession';
import { ActivitySession } from '../ActivitySession/ActivitySession';
import { StatsBar } from './StatsBar';
import { ActivityIcon, LockedActivityIcon } from '../../../../shared/components';

// Icons
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" fill="#198754" viewBox="0 0 448 512">
    <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
  </svg>
);

const FireIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" fill="#fd7e14" viewBox="0 0 448 512">
    <path d="M159.3 5.4c7.8-7.3 19.9-7.2 27.7 .1c27.6 25.9 53.5 53.8 77.7 84c11-14.4 23.5-30.1 37-42.9c7.9-7.4 20.1-7.4 28 .1c34.6 33 63.9 76.6 84.5 118c20.3 40.8 33.8 82.5 33.8 111.9C448 404.2 348.2 512 224 512C98.4 512 0 404.1 0 276.5c0-38.4 17.8-85.3 45.4-131.7C73.3 97.7 112.7 48.6 159.3 5.4zM225.7 416c25.3 0 47.7-7 68.8-21c42.1-29.4 53.4-88.2 28.1-134.4c-4.5-9-16-9.6-22.5-2l-25.2 29.3c-6.6 7.6-18.5 7.4-24.7-.5c-16.5-21-46-58.5-62.8-79.8c-6.3-8-18.3-8.1-24.7-.1c-33.8 42.5-50.8 69.3-50.8 99.4C112 375.4 162.6 416 225.7 416z"/>
  </svg>
);

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" fill="#6f42c1" viewBox="0 0 576 512">
    <path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/>
  </svg>
);

const TargetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#6c757d" viewBox="0 0 512 512" style={{ flexShrink: 0 }}>
    <path d="M448 256A192 192 0 1 0 64 256a192 192 0 1 0 384 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm256 80a80 80 0 1 0 0-160 80 80 0 1 0 0 160zm0-224a144 144 0 1 1 0 288 144 144 0 1 1 0-288zM224 256a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/>
  </svg>
);

// Format number with delimiter
const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

export const Dashboard = ({ data, onDataRefresh }) => {
  const { t } = useTranslation();
  const { child, daily_target, stats, current_activity, next_activities, active_session, pending_skip_request } = data;
  const { createSession } = useActivitySession();

  // State for active session - initialize from API data if an active session exists
  const [activeSession, setActiveSession] = useState(active_session || null);
  const [sessionActivity, setSessionActivity] = useState(active_session ? current_activity : null);
  const [sessionNextActivities, setSessionNextActivities] = useState(active_session ? next_activities : []);
  const [isStarting, setIsStarting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [skipRequestPending, setSkipRequestPending] = useState(!!pending_skip_request);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isIgnoringActivity, setIsIgnoringActivity] = useState(false);

  // Check if subscription is needed for current activity
  const needsSubscription = !child.has_active_subscription && current_activity && !current_activity.is_free;

  // Sync skipRequestPending state with pending_skip_request prop when session changes
  // This ensures the skip request pending state is tied to the current session
  useEffect(() => {
    setSkipRequestPending(!!pending_skip_request);
  }, [active_session?.id, pending_skip_request]);

  // Handle starting an activity
  const handleStartActivity = useCallback(async () => {
    if (!current_activity || isStarting) return;

    // Show subscription modal if kid needs subscription for this activity
    if (needsSubscription) {
      setShowSubscriptionModal(true);
      return;
    }

    setIsStarting(true);
    try {
      const result = await createSession.mutateAsync(current_activity.id);
      // Store session data and switch to activity session view
      setActiveSession(result.session);
      setSessionActivity(result.activity);
      setSessionNextActivities(result.next_activities || []);
    } catch (error) {
      console.error('Failed to start activity:', error);
      alert(error.message || 'Failed to start activity');
    } finally {
      setIsStarting(false);
    }
  }, [current_activity, createSession, isStarting, needsSubscription]);

  // Handle session completion - return to dashboard view
  const handleSessionComplete = useCallback((result) => {
    setActiveSession(null);
    setSessionActivity(null);
    setSessionNextActivities([]);
    // Show success message if provided
    if (result?.message) {
      setSuccessMessage(result.message);
      // Auto-dismiss after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    }
    // Refresh dashboard data
    if (onDataRefresh) {
      onDataRefresh();
    }
  }, [onDataRefresh]);

  // Handle ignoring a paid activity and moving to the next one
  const handleIgnoreActivity = useCallback(async () => {
    if (!current_activity || isIgnoringActivity) return;

    setIsIgnoringActivity(true);
    try {
      const response = await fetch(`/api/children/curriculum_activities/${current_activity.id}/skip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      });

      if (response.ok) {
        setShowSubscriptionModal(false);
        // Refresh dashboard data to get the next activity
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        console.error('Failed to skip activity');
      }
    } catch (error) {
      console.error('Failed to skip activity:', error);
    } finally {
      setIsIgnoringActivity(false);
    }
  }, [current_activity, isIgnoringActivity, onDataRefresh]);

  // Render activity session if one is active
  if (activeSession && sessionActivity) {
    return (
      <div id="activity_panel" className="container-fluid p-4">
        {/* Header */}
        <header className="mb-4">
          <h1 className="fs-2 fw-bold mb-1">{t('children.dashboard.learning_path.title')}</h1>
        </header>

        {/* Stats Bar - Show progress while learning */}
        <StatsBar child={child} stats={stats} dailyTarget={daily_target} />

        <ActivitySession
          session={activeSession}
          activity={sessionActivity}
          nextActivities={sessionNextActivities}
          onComplete={handleSessionComplete}
          initialSkipRequestPending={skipRequestPending}
        />
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage(null)}
            aria-label="Close"
          ></button>
        </div>
      )}


      {/* Header */}
      <header className="mb-4">
        <h1 className="fs-2 fw-bold mb-1">{t('children.dashboard.welcome_back', { name: child.username })}</h1>
        <p className="text-secondary mb-0">{t('children.dashboard.ready_for_activities')}</p>
      </header>

      {/* Stats Cards */}
      <section className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card border-0 rounded-4 h-100">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-secondary small mb-1">{t('children.dashboard.stats.activities_completed')}</p>
                  <h2 className="fs-1 fw-bold mb-0">{stats.completed_sessions_count}</h2>
                </div>
                <div className="bg-success-subtle rounded-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                  <CheckIcon />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-0 rounded-4 h-100">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-secondary small mb-1">{t('children.dashboard.stats.current_streak')}</p>
                  <h2 className="fs-1 fw-bold mb-0">{child.current_streak} {t('children.dashboard.stats.days')}</h2>
                </div>
                <div className="bg-warning-subtle rounded-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                  <FireIcon />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card border-0 rounded-4 h-100">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-secondary small mb-1">{t('children.dashboard.stats.total_xp')}</p>
                  <h2 className="fs-1 fw-bold mb-0">{formatNumber(daily_target.achieved_xp)}</h2>
                </div>
                <div className="bg-primary-subtle rounded-3 d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px' }}>
                  <StarIcon />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Daily Goal Progress Bar */}
      <section className="mb-4" data-testid="daily-goal-progress">
        <div
          className="rounded-3 p-3"
          style={{ background: '#f9fafb' }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-2">
              <TargetIcon />
              <span className="fw-semibold" style={{ fontSize: '14px', color: '#374151' }}>
                {t('children.dashboard.stats.daily_target')}
              </span>
            </div>
            <span
              className="fw-semibold"
              style={{
                fontSize: '14px',
                color: daily_target.achieved_xp >= daily_target.target_xp ? '#16a34a' : '#667eea'
              }}
            >
              {formatNumber(daily_target.achieved_xp)}/{formatNumber(daily_target.target_xp)} XP
            </span>
          </div>
          <div
            className="rounded-pill"
            style={{ background: '#e5e7eb', height: '10px', width: '100%' }}
          >
            <div
              className="rounded-pill"
              style={{
                background: daily_target.achieved_xp >= daily_target.target_xp ? '#22c55e' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                height: '10px',
                width: `${Math.min((daily_target.achieved_xp / daily_target.target_xp) * 100, 100)}%`,
                transition: 'width 0.3s ease',
                boxShadow: daily_target.achieved_xp >= daily_target.target_xp ? '0px 1px 2px #bbf7d0' : 'none'
              }}
            ></div>
          </div>
          {daily_target.achieved_xp >= daily_target.target_xp && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '14px' }}></i>
              <span className="text-success fw-medium" style={{ fontSize: '12px' }}>
                {t('children.dashboard.daily_goal.completed')}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Learning Path */}
      <section>
        <h2 className="fs-4 fs-lg-3 fw-bold mb-3 mb-lg-4">{t('children.dashboard.learning_path.title')}</h2>

        {/* Current Activity Panel */}
        {current_activity ? (
          <div id="activity_panel" className="card border-2 border-primary rounded-4 mb-3">
            <div className="card-body p-4">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-3 gap-3">
                <div className="d-flex flex-column flex-md-row align-items-start gap-3 w-100">
                  <div className="bg-primary-subtle rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 mx-auto mx-md-0" style={{ width: '64px', height: '64px' }}>
                    <ActivityIcon iconUrl={current_activity.icon_url} name={current_activity.name} size={40} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h3 className="fs-5 fw-bold mb-0">{current_activity.name}</h3>
                      {current_activity.service_account_name && (
                        <span className="badge bg-primary-subtle text-primary rounded-2 px-2 py-1 fw-medium" style={{ fontSize: '14px' }}>
                          {current_activity.service_account_name}
                        </span>
                      )}
                    </div>
                    <p className="text-secondary mb-2">
                      {current_activity.course_names || current_activity.service_name}
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                      <span className="badge bg-success-subtle text-success">
                        {t('children.dashboard.learning_path.xp_reward', { xp: current_activity.reward_xp })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleStartActivity}
                disabled={isStarting}
                className="btn btn-primary w-100 fw-semibold py-3"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  minHeight: '56px',
                  lineHeight: '1.5'
                }}
              >
                {isStarting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t('common.loading')}
                  </>
                ) : (
                  t('children.dashboard.learning_path.start_activity')
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="alert alert-info">
            <p className="mb-0">{t('children.dashboard.learning_path.no_activities')}</p>
          </div>
        )}

        {/* Next Activities */}
        {next_activities && next_activities.length > 0 && (
          <div className="row g-3">
            {next_activities.map((activity, index) => (
              <div key={`${activity.id}-${index}`} className="col-md-4">
                <div className="card border-0 rounded-4 opacity-75 locked-activity-card">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center gap-3">
                      <LockedActivityIcon iconUrl={activity.icon_url} name={activity.name} size={48} />
                      <div className="flex-grow-1" style={{ minWidth: 0, overflow: 'hidden' }}>
                        <h6 className="fw-bold mb-0" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{activity.name}</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Subscription Required Modal */}
      {showSubscriptionModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} data-testid="subscription-required-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">{t('children.dashboard.subscription_modal.title')}</h5>
              </div>
              <div className="modal-body">
                <p className="text-secondary">
                  {t('children.dashboard.subscription_modal.message', { activity_name: current_activity?.name })}
                </p>
                <p className="text-secondary mb-0">
                  {t('children.dashboard.subscription_modal.ask_guardian')}
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleIgnoreActivity}
                  disabled={isIgnoringActivity}
                  data-testid="ignore-activity-btn"
                >
                  {isIgnoringActivity ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : null}
                  {t('children.dashboard.subscription_modal.ignore_activity')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
